import type { CSHFormData } from "../../components/forms/CSHForm";
import {
    assignSitesToGroupGrid,
    assignUsersToGroupGrid,
    removeSitesFromGroupGrid,
    removeUsersFromGroupGrid,
} from "../../services/csh/csh.service";
import type {
    AssignedUserSelection, CreateCshGroupRequest, CshGroup, CshGroupSite, CustomerSelection, SiteSelection,
} from "../../services/csh/csh.types";

export interface CshCustomerOption {
    id: number;
    customerId: number;
    customerName: string;
    sites?: SiteSelection[];
}
export type CshFormErrors = Partial<Record<"name" | "assignedTo" | "customers", string>>;

export const getCshErrorMessage = (error: unknown, fallback: string): string =>
    error instanceof Error && error.message ? error.message : fallback;

export const getAssignedUserId = (user: string | AssignedUserSelection): string =>
    typeof user === "string" ? user : String(user.id ?? user.userId ?? "");

export function getAssignedUserLabel(user: string | AssignedUserSelection): string {
    if (typeof user === "string") return user;
    if (user.label) return user.label;
    const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
    return (user.employeeId ? `${name} - ${user.employeeId}` : name) || getAssignedUserId(user);
}

export function groupSitesByCustomer(sites: CshGroupSite[]): CustomerSelection[] {
    const customers = new Map<number, CustomerSelection>();
    for (const site of sites) {
        const id = site.customerId ?? 0;
        let customer = customers.get(id);
        if (!customer) {
            customer = { customerId: id, customerName: site.customerName ?? "N/A", sites: [] };
            customers.set(id, customer);
        }
        customer.sites.push({ siteId: site.siteIdentifierRegistryId, siteName: site.siteName });
    }
    return [...customers.values()];
}

export function mapGroupToCshForm(group: CshGroup | null): CSHFormData {
    return {
        name: group?.name ?? "",
        assignedTo: (group?.assignedTo ?? []).map(user => ({ id: getAssignedUserId(user), label: getAssignedUserLabel(user) })),
        customers: group?.customers.length ? group.customers.map(customer => ({
            customerId: customer.customerId ? String(customer.customerId) : "",
            selectedSites: customer.sites.map(site => String(site.siteId)),
        })) : [{ customerId: "", selectedSites: [] }],
    };
}

export function validateCshForm(data: CSHFormData): CshFormErrors {
    const errors: CshFormErrors = {};
    if (!data.name.trim()) errors.name = "Name is required";
    else if (data.name.length < 2) errors.name = "Name must be at least 2 characters";
    else if (data.name.length > 100) errors.name = "Name must be less than 100 characters";
    if (!data.assignedTo.length) errors.assignedTo = "At least one user must be assigned";
    if (!data.customers.some(customer => customer.customerId)) errors.customers = "Please select a customer";
    else if (!data.customers.some(customer => customer.customerId && customer.selectedSites.length)) {
        errors.customers = "Please select at least one site";
    }
    return errors;
}

export function buildCshGroupPayload(data: CSHFormData, customers: CshCustomerOption[]): CreateCshGroupRequest {
    return {
        name: data.name,
        assignedTo: data.assignedTo.map(user => user.id).join(","),
        customers: data.customers.filter(customer => customer.customerId).map(selection => {
            const customerId = Number(selection.customerId);
            const customer = customers.find(customer => customer.customerId === customerId);
            return {
                customerId,
                customerName: customer?.customerName ?? "",
                sites: selection.selectedSites.map(id => ({
                    siteId: Number(id),
                    siteName: customer?.sites?.find(site => String(site.siteId) === id)?.siteName ?? "",
                })),
            };
        }),
    };
}

export function getAddedAndRemovedIds(current: string[], initial: string[]) {
    return {
        removedIds: [...new Set(initial)].filter(id => !current.includes(id)),
        addedIds: [...new Set(current)].filter(id => !initial.includes(id)),
    };
}

export async function syncCshAssignments(groupId: string, data: CSHFormData, initialGroup: CshGroup | null): Promise<string[]> {
    const users = getAddedAndRemovedIds(data.assignedTo.map(user => user.id), (initialGroup?.assignedTo ?? []).map(getAssignedUserId));
    const sites = getAddedAndRemovedIds(
        data.customers.flatMap(customer => customer.selectedSites),
        (initialGroup?.customers ?? []).flatMap(customer => customer.sites.map(site => String(site.siteId))),
    );
    const operations = [
        { ids: users.removedIds, action: removeUsersFromGroupGrid, fallback: "Failed to remove users from CSH group" },
        { ids: users.addedIds, action: assignUsersToGroupGrid, fallback: "Failed to assign users to the CSH group" },
        { ids: sites.removedIds, action: removeSitesFromGroupGrid, fallback: "Failed to remove sites from CSH group" },
        { ids: sites.addedIds, action: assignSitesToGroupGrid, fallback: "Failed to assign sites to the CSH group" },
    ];
    const errors: string[] = [];
    for (const operation of operations) {
        if (!operation.ids.length) continue;
        try {
            const response = await operation.action(groupId, operation.ids);
            if (!response.success) throw new Error(response.error?.message || operation.fallback);
        } catch (error: unknown) {
            errors.push(getCshErrorMessage(error, operation.fallback));
        }
    }
    return errors;
}

export function buildAssignedEventUsers(data: CSHFormData, users: AssignedUserSelection[]) {
    return data.assignedTo.map(assigned => {
        const user = users.find(user => getAssignedUserId(user) === assigned.id);
        const [name, employeeId] = assigned.label.split(" - ");
        const [firstName, ...lastName] = name.trim().split(/\s+/);
        return {
            firstName: user?.firstName ?? (firstName || null),
            lastName: user?.lastName ?? (lastName.join(" ") || null),
            employeeId: user?.employeeId ?? (employeeId || null),
            display: assigned.label,
        };
    });
}
